import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"
import { Link } from "react-router-dom"
import { gameRoles } from "@/utils/gameRoles"
import {
  ChartTooltip,
  EmptyHint,
  Panel,
  formatDate,
  formatMoney,
  formatNumber,
} from "./statsUi"

const placementLabel = (placement: number) => {
  if (placement === 1) return "1st"
  if (placement === 2) return "2nd"
  if (placement === 3) return "3rd"
  return `${placement}th`
}

// The recent-games feed, newest first, plus the rating line it produced. The
// stored feed is capped server-side, so this is a recent-form view rather than
// a complete match history — the full archive lives on the History page.
const HistoryTab: React.FC<{ stats: IUserLifetimeStatsResponse }> = ({
  stats,
}) => {
  const recent = stats.recentGames || []
  // Stored newest-first; a rating line has to read left-to-right in time.
  const ratingSeries = [...recent].reverse().map((game, index) => ({
    game: `#${index + 1}`,
    rating: game.ratingAfter,
    change: game.ratingChange,
  }))

  return (
    <div className="flex flex-col gap-4 sm:gap-2.5">
      <Panel
        title="Rating History"
        subtitle={`Last ${recent.length} recorded game${recent.length === 1 ? "" : "s"}`}
        icon="fa-arrow-trend-up"
        accent="#FBBF24"
      >
        {ratingSeries.length < 2 ? (
          <EmptyHint text="Finish a couple of games to plot your rating." />
        ) : (
          <div className="h-60 sm:h-44 mobile:h-[136px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={ratingSeries}>
                <defs>
                  <linearGradient id="ratingFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FBBF24" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#FBBF24" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.07)"
                  vertical={false}
                />
                <XAxis
                  dataKey="game"
                  tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={["dataMin - 30", "dataMax + 30"]}
                  tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={46}
                />
                <ChartTooltip />
                <Area
                  type="monotone"
                  dataKey="rating"
                  name="Rating"
                  stroke="#FBBF24"
                  strokeWidth={2}
                  fill="url(#ratingFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Panel>

      <Panel
        title="Recent Games"
        subtitle="Result, role and rating movement"
        icon="fa-clock-rotate-left"
        accent="#38BDF8"
      >
        {recent.length === 0 ? (
          <EmptyHint text="No finished games recorded yet. Play a game to start your record." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[46rem] mobile:min-w-[620px] text-left">
              <thead>
                <tr className="border-b border-white/10 text-[0.65rem] mobile:text-[10px]/[1.4] uppercase tracking-wider text-white/40">
                  <th className="pb-2 mobile:pb-[6px] pr-3 font-semibold">Result</th>
                  <th className="pb-2 mobile:pb-[6px] pr-3 font-semibold">Role</th>
                  <th className="pb-2 mobile:pb-[6px] pr-3 font-semibold">Net Worth</th>
                  <th className="pb-2 mobile:pb-[6px] pr-3 font-semibold">Turns</th>
                  <th className="pb-2 mobile:pb-[6px] pr-3 font-semibold">Ended</th>
                  <th className="pb-2 mobile:pb-[6px] pr-3 font-semibold">Rating</th>
                  <th className="pb-2 mobile:pb-[6px] pr-3 font-semibold">Date</th>
                  <th className="pb-2 mobile:pb-[6px] font-semibold" />
                </tr>
              </thead>
              <tbody>
                {recent.map((game) => {
                  const role = gameRoles.find((item) => item.id === game.role)
                  return (
                    <tr
                      key={`${game.gameId}-${game.playedAt}`}
                      className="border-b border-white/5 text-sm sm:text-xs mobile:text-[12px]/[1.4] last:border-b-0"
                    >
                      <td className="py-2.5 mobile:py-[7px] pr-3">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs mobile:text-[11px]/[1.4] font-semibold ${
                            game.won
                              ? "bg-amber-500/20 text-amber-200"
                              : game.wentBankrupt
                                ? "bg-rose-500/20 text-rose-200"
                                : "bg-white/5 text-white/60"
                          }`}
                        >
                          <i
                            className={`fas ${
                              game.won
                                ? "fa-trophy"
                                : game.wentBankrupt
                                  ? "fa-skull"
                                  : "fa-flag-checkered"
                            } text-[0.65rem] mobile:text-[10px]/[1.4]`}
                          />
                          {game.won
                            ? "Win"
                            : `${placementLabel(game.placement)} of ${game.playerCount}`}
                        </span>
                      </td>
                      <td className="py-2.5 mobile:py-[7px] pr-3">
                        <span style={{ color: role?.color || "#94A3B8" }}>
                          {role?.title || "No Role"}
                        </span>
                      </td>
                      <td className="py-2.5 mobile:py-[7px] pr-3 text-white/80">
                        {formatMoney(game.networth)}
                      </td>
                      <td className="py-2.5 mobile:py-[7px] pr-3 text-white/60">
                        {formatNumber(game.turns)}
                      </td>
                      <td className="py-2.5 mobile:py-[7px] pr-3 text-white/50">
                        {game.endReason === "bankruptcy"
                          ? "Knockout"
                          : game.endReason === "networth"
                            ? "Net worth"
                            : "—"}
                      </td>
                      <td className="py-2.5 mobile:py-[7px] pr-3">
                        <span className="text-white/80">
                          {formatNumber(game.ratingAfter)}
                        </span>{" "}
                        <span
                          className={
                            game.ratingChange >= 0
                              ? "text-emerald-400"
                              : "text-rose-400"
                          }
                        >
                          ({game.ratingChange >= 0 ? "+" : ""}
                          {game.ratingChange})
                        </span>
                      </td>
                      <td className="py-2.5 mobile:py-[7px] pr-3 text-white/40">
                        {formatDate(game.playedAt)}
                      </td>
                      <td className="py-2.5 mobile:py-[7px]">
                        <Link
                          to={`/game-history/${game.gameId}`}
                          className="text-xs mobile:text-[11px]/[1.4] text-sky-300 hover:text-sky-200 hover:underline"
                        >
                          Replay
                        </Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  )
}

export default HistoryTab
